'use client';
import React, { useState } from 'react';
import { useUser, useReverification } from '@clerk/nextjs';
import { isClerkRuntimeError, isReverificationCancelledError } from '@clerk/nextjs/errors';
import { toast } from 'sonner';
import clsx from 'clsx';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { KeenIcon } from '../../common/keenicons';

function PasswordField({
    label,
    name,
    value,
    onChange,
    onBlur,
    show,
    toggleShow,
    placeholder,
    error,
    touched,
}: {
    label: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    show: boolean;
    toggleShow: () => void;
    placeholder: string;
    error?: string;
    touched?: boolean;
}) {
    return (
        <div className="w-full">
            <div className="flex flex-wrap lg:flex-nowrap gap-2.5">
                <label className="form-label flex items-center gap-1 max-w-[200px] text-gray-600 font-normal text-sm">
                    {label}
                </label>
                <div className='flex-1'>
                    <label className="input">
                        <input
                            type={show ? 'text' : 'password'}
                            name={name}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            className="form-control"
                            placeholder={placeholder}
                        />
                        <button type="button" onClick={toggleShow} className="btn btn-icon">
                            <KeenIcon icon="eye" className={clsx('text-gray-500', { hidden: show })} />
                            <KeenIcon icon="eye-slash" className={clsx('text-gray-500', { hidden: !show })} />
                        </button>
                    </label>
                    {error && touched &&
                        <span role="alert" className="text-danger text-xs mt-1">{error}</span>
                    }
                </div>
            </div>
        </div>
    );
}

// Main Component
export default function ChangePassword() {
    const { user } = useUser();
    const intl = useIntl();

    const changePassword = useReverification(async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
        await user?.updatePassword({
            currentPassword,
            newPassword,
            signOutOfOtherSessions: true,
        });
    });

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        },
        validationSchema: Yup.object({
            currentPassword: Yup.string()
                .required(intl.formatMessage({ id: 'CHANGE_PASSWORD.VALIDATION.CURRENT_REQUIRED' })),
            newPassword: Yup.string()
                .required(intl.formatMessage({ id: 'CHANGE_PASSWORD.VALIDATION.NEW_REQUIRED' })),
            confirmNewPassword: Yup.string()
                .required(intl.formatMessage({ id: 'CHANGE_PASSWORD.VALIDATION.CONFIRM_REQUIRED' })),
        }),

        onSubmit: async (values, { resetForm, setSubmitting }) => {
            if (values.newPassword !== values.confirmNewPassword) {
                toast.error(intl.formatMessage({ id: 'CHANGE_PASSWORD.ERROR.PASSWORD_MISMATCH' }));
                setSubmitting(false);
                return;
            }

            try {
                await changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword });
                toast.success(intl.formatMessage({ id: 'CHANGE_PASSWORD.SUCCESS' }));
                resetForm();
            } catch (err: any) {
                if (isClerkRuntimeError(err) && isReverificationCancelledError(err)) {
                    toast.error(intl.formatMessage({ id: 'CHANGE_PASSWORD.ERROR.REVERIFICATION_CANCELLED' }));
                    return;
                }
                toast.error(err.errors?.[0]?.message || intl.formatMessage({ id: 'CHANGE_PASSWORD.ERROR.GENERIC' }));
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div>
            <div className="card">
                <div className="flex items-center justify-between flex-wrap px-5 min-h-14 gap-2.5 border-b border-border card-header">
                    <h3 className="text-base font-semibold leading-none tracking-tight card-title">
                        {intl.formatMessage({ id: 'CHANGE_PASSWORD.TITLE' })}
                    </h3>
                </div>

                <form onSubmit={formik.handleSubmit} className="grow p-5 grid gap-5 card-content">
                    <PasswordField
                        label={intl.formatMessage({ id: 'CHANGE_PASSWORD.CURRENT' })}
                        name="currentPassword"
                        value={formik.values.currentPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        show={showOldPassword}
                        toggleShow={() => setShowOldPassword(!showOldPassword)}
                        placeholder={intl.formatMessage({ id: 'CHANGE_PASSWORD.PLACEHOLDER.CURRENT' })}
                        error={formik.errors.currentPassword}
                        touched={formik.touched.currentPassword}
                    />

                    <PasswordField
                        label={intl.formatMessage({ id: 'CHANGE_PASSWORD.NEW' })}
                        name="newPassword"
                        value={formik.values.newPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        show={showNewPassword}
                        toggleShow={() => setShowNewPassword(!showNewPassword)}
                        placeholder={intl.formatMessage({ id: 'CHANGE_PASSWORD.PLACEHOLDER.NEW' })}
                        error={formik.errors.newPassword}
                        touched={formik.touched.newPassword}
                    />

                    <PasswordField
                        label={intl.formatMessage({ id: 'CHANGE_PASSWORD.CONFIRM' })}
                        name="confirmNewPassword"
                        value={formik.values.confirmNewPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        show={showConfirmPassword}
                        toggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
                        placeholder={intl.formatMessage({ id: 'CHANGE_PASSWORD.PLACEHOLDER.CONFIRM' })}
                        error={formik.errors.confirmNewPassword}
                        touched={formik.touched.confirmNewPassword}
                    />

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
                        >
                            {formik.isSubmitting
                                ? intl.formatMessage({ id: 'CHANGE_PASSWORD.LOADING' })
                                : intl.formatMessage({ id: 'CHANGE_PASSWORD.SUBMIT' })
                            }
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
}
